import { useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'

import type { Gebied } from '@models/Gebied'
import type { GroupedOption } from '@models/GroupedOption'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { SelectObject } from '../../../core/components/SelectObject'
import { useOrganisatiesByGebied } from '../../../projectdossier/hooks/useOrganisatiesByGebied'
import type { Projectmelding } from '../../../projectmelding/types/Projectmelding'
import { useCreateProjectdossier } from '../../hooks/useCreateProjectdossier'

interface Props {
  projectmelding?: Projectmelding
  onClose: () => void
  gebieden: Gebied[]
}

export const CreateProjectdossierDialog = ({
  projectmelding,
  onClose,
  gebieden,
}: Props) => {
  const { combi } = useAuthenticatedUser()

  const [name, setName] = useState('')
  const [referenceId, setReferenceId] = useState('')
  const [projectmeldingId, setProjectmeldingId] = useState('')
  const [indienerNaam, setIndienerNaam] = useState('')
  const [indienerOrg, setIndienerOrg] = useState('')
  const [location, setLocation] = useState('')
  const [contractgebied, setContractgebied] = useState<any>(null)
  const [errors, setErrors] = useState({})
  const [involvedParties, setInvolvedParties] = useState<GroupedOption[]>([])

  const { data: organisaties, isLoading: isLoadingOrganisaties } =
    useOrganisatiesByGebied({
      gebiedscode: contractgebied?.code || '',
    })

  const { mutate: createProjectdossier } = useCreateProjectdossier()

  const validate = () => {
    const nameError = !name
    const indienerNaamError = !indienerNaam
    const indienerOrgError = !indienerOrg
    const locationError = !location
    const contractgebiedError = !contractgebied

    setErrors({
      nameError,
      indienerNaamError,
      indienerOrgError,
      locationError,
      contractgebiedError,
    })

    return !(
      nameError ||
      indienerNaamError ||
      indienerOrgError ||
      contractgebiedError ||
      locationError
    )
  }

  useEffect(() => {
    if (projectmelding) {
      const {
        projectName,
        description,
        gender,
        initials,
        middleName,
        lastName,
        companyName,
        contractGebiedCode,
        id,
      } = projectmelding

      setName(projectName)
      setLocation(description)
      setIndienerNaam(`${gender} ${initials} ${middleName} ${lastName}`)
      setIndienerOrg(companyName)
      setContractgebied(gebieden.find((g) => g.code === contractGebiedCode))
      setProjectmeldingId(id)

      setErrors({})
      setReferenceId('')
      setInvolvedParties(organisaties.filter((x) => x.label === combi))
    }
  }, [projectmelding, gebieden, combi, organisaties])

  useEffect(() => {
    setInvolvedParties(organisaties.filter((x) => x.label === combi))
  }, [organisaties, combi])

  const handleSubmitClick = () => {
    if (validate()) {
      const body: any = {
        naam: name,
        externeReferentie: referenceId,
        locatieOmschrijving: location,
        organisaties: involvedParties.map((x: any) => x.label),
        gebiedscode: contractgebied.code,
        indiener: {
          naam: indienerNaam,
          organisatie: indienerOrg,
        },
      }

      if (projectmelding?.id) {
        body.projectmeldingId = projectmelding.id
      }

      createProjectdossier(
        { projectdossier: body },
        {
          onSuccess: () => {
            onClose()
          },
        },
      )
    }
  }

  const handleInvolvedPartiesChange = (value) => {
    const partyToKeep = involvedParties.find((x) => x.label === combi)

    if (value.length === 0 && partyToKeep) {
      setInvolvedParties([partyToKeep])
    } else {
      setInvolvedParties(value)
    }
  }

  return (
    <>
      <Dialog open fullWidth maxWidth={'md'}>
        <DialogTitle>Aanmaken projectdossier</DialogTitle>
        <DialogContent>
          <Box
            sx={{ width: '95%', marginLeft: 'auto', marginRight: 'auto', p: 1 }}
          >
            <FormControl sx={{ width: '100%', p: 1 }}>
              <TextField
                variant="standard"
                data-testid="inputField"
                value={name}
                label={'Dossiernaam'}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                onChange={(e) => setName(e.target.value)}
                error={errors['nameError']}
              />
            </FormControl>

            <FormControl sx={{ width: '100%', p: 1 }}>
              <TextField
                variant="standard"
                data-testid="inputField"
                value={referenceId}
                label={'Externe referentie'}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                onChange={(e) => setReferenceId(e.target.value)}
                error={errors['referenceIdError']}
              />
            </FormControl>

            {projectmelding?.id && (
              <FormControl sx={{ width: '100%', p: 1 }}>
                <TextField
                  variant="standard"
                  value={projectmeldingId}
                  label={'Projectmelding id'}
                  disabled
                />
              </FormControl>
            )}

            <FormControl sx={{ width: '100%', p: 1 }}>
              <SelectObject
                disabled={!!projectmelding?.id}
                options={gebieden}
                value={contractgebied}
                onChange={setContractgebied}
                label="Contractgebied"
                error={errors['contractgebiedError']}
              />
            </FormControl>

            <FormControl sx={{ width: '100%', p: 1 }}>
              <TextField
                variant="standard"
                data-testid="inputField"
                value={location}
                multiline
                rows={3}
                label={`Beschrijving (${location.length}/500)`}
                slotProps={{ htmlInput: { maxLength: 500 } }}
                onChange={(e) => setLocation(e.target.value)}
                error={errors['locationError']}
              />
            </FormControl>

            <FormControl sx={{ width: '100%', p: 1 }}>
              <SelectObject
                multiple
                disabled={!contractgebied || isLoadingOrganisaties}
                options={organisaties}
                value={involvedParties}
                onChange={handleInvolvedPartiesChange}
                label="Betrokken partijen"
                helperText="De combi geldt altijd als betrokken partij"
              />
            </FormControl>

            <FormControl sx={{ width: '100%', p: 1 }}>
              <TextField
                variant="standard"
                data-testid="inputField"
                value={indienerNaam}
                label={'Indiener'}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                onChange={(e) => setIndienerNaam(e.target.value)}
                error={errors['indienerNaamError']}
              />
            </FormControl>

            <FormControl sx={{ width: '100%', p: 1 }}>
              <TextField
                variant="standard"
                data-testid="inputField"
                value={indienerOrg}
                label={'Indienende organisatie'}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                onChange={(e) => setIndienerOrg(e.target.value)}
                error={errors['indienerOrgError']}
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onClose}>
            Annuleren
          </Button>
          <Button color="primary" onClick={handleSubmitClick}>
            Aanmaken
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
